import { ModelType, PublicationSheet, getAutoTocTitle } from '@livv/models';
import { Box, SxProps, Typography } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import { FC, useState, ReactNode } from 'react';
import { formatSubtitleElements } from '../../utils';
import NavBreadcrumbs from '../Breadcrumbs/NavBreadcrumbs';
import SideBar from '../SideBar';
import CollapseExpandButton from '../SideBar/CollapseExpandButton';
import TitleTree from '../SideBar/TitleTree';
import { getPublicationMetaDescription } from './utils';
import DocumentLayout from '@components/DocumentLayout';
import { useBookPageContext } from '@utils/context/book';
import { useBookPathSlugs, useBookSheetSlugs } from '@utils/hooks/useBookSlugs';
import {
    useLegitextMetaDescription,
    usePathTitles,
} from '@utils/hooks/usePathTitlesAndMetaDescription';

const titleWrapperSx: SxProps = {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    whiteSpace: 'normal',
    width: '100%',
};

interface BookContainerProps {
    children: ReactNode;
}

const BookContainer: FC<BookContainerProps> = ({ children }) => {
    const { bookPath } = useBookPathSlugs();
    const { bookId, sheetId } = useBookSheetSlugs();
    const { bookData, bookMeta, isLegitext, sheetData } = useBookPageContext();
    const { t, lang } = useTranslation('books');

    const focusedNodeIds = bookPath?.split('/') ?? [];
    const [expanded, setExpanded] = useState<string[]>(focusedNodeIds);

    const pathTitles = usePathTitles(focusedNodeIds, bookData, bookMeta).filter(Boolean);
    const modelType = isLegitext ? ModelType.LAW_TEXT : ModelType.PUBLICATION_SHEET;

    const getNodeTypography = (): string => {
        return bookData.getFullNodeTypography(
            focusedNodeIds,
            bookMeta.language,
            bookMeta.dummyLevel,
        );
    };

    const legitextDescription = useLegitextMetaDescription(
        isLegitext,
        pathTitles as Array<{ title: string }>,
    );
    const description = isLegitext
        ? legitextDescription
        : getPublicationMetaDescription(
              bookMeta.theme,
              bookMeta.title,
              t,
              sheetData as PublicationSheet | undefined,
          );

    const currentNode = focusedNodeIds?.length > 0 && bookData.getNode(focusedNodeIds);
    let bookMetaTitle = bookMeta.title;

    if (!focusedNodeIds.length && bookMeta.hasAutoToc) {
        bookMetaTitle = `${getAutoTocTitle(bookMeta.language)} | ${bookMeta.title}`;
    }

    if (currentNode) {
        bookMetaTitle = `${currentNode.title} | ${bookMeta.title} (${getNodeTypography()})`;
    }

    if (sheetData) {
        bookMetaTitle = `${sheetData.title} | ${bookMeta.title} (${getNodeTypography()})`;
    }

    const renderNavBreadcrumbs = isLegitext ? null : <NavBreadcrumbs />;

    return (
        <DocumentLayout
            breadcrumbs={renderNavBreadcrumbs}
            documentId={sheetId || bookId}
            headTitle={bookMetaTitle}
            maxContentWidth={false}
            modelType={modelType}
            pageDescription={description}
            subtitleElements={formatSubtitleElements(bookMeta, isLegitext, { lang, t })}
            title={bookMeta.title}
            fullHeight
        >
            <Box sx={{ display: { md: 'flex' } }}>
                <SideBar>
                    <Box data-test-id="book-sidebar" px={3}>
                        <Box sx={titleWrapperSx}>
                            <Typography
                                component="div"
                                sx={{ color: 'grey.100', fontWeight: 600, mb: 1 }}
                                variant="body2"
                            >
                                {t('sidebar.title')}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'block',
                                overflowX: 'hidden',
                                overflowY: 'auto',
                            }}
                        >
                            <TitleTree
                                expanded={expanded}
                                focusedPath={bookPath}
                                hasAutoToc={Boolean(bookMeta.hasAutoToc)}
                                setExpanded={setExpanded}
                                treeDataNodes={bookData.content}
                                treeId={bookId}
                            />
                        </Box>
                    </Box>
                    <CollapseExpandButton
                        setExpanded={setExpanded}
                        treeDataNodes={bookData.content}
                    />
                </SideBar>
                <Box
                    component="main"
                    data-test-id={`${bookId}-book`}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        justifyContent: 'space-between',
                        mt: 3,
                    }}
                >
                    {children}
                </Box>
            </Box>
        </DocumentLayout>
    );
};

export default BookContainer;
